import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const INPUT = "mt-1 w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm text-white";

// Admin Console: edit the Resend daily / monthly send caps.
export function EmailLimitsSetting() {
  const [saved, setSaved] = useState(null);
  const [daily, setDaily] = useState("");
  const [monthly, setMonthly] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/email-limits").then(({ data }) => {
      setSaved(data); setDaily(String(data.daily)); setMonthly(String(data.monthly));
    }).catch(() => setSaved(null));
  }, []);

  const d = parseInt(daily, 10);
  const m = parseInt(monthly, 10);
  const valid = d > 0 && m > 0 && d <= m;
  const changed = saved && (d !== saved.daily || m !== saved.monthly);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/email-limits", { daily: d, monthly: m });
      setSaved(data);
      toast.success("Email limits updated");
      setConfirm(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save limits"); }
    finally { setBusy(false); }
  };

  if (!saved) return null;
  return (
    <section className="card-surface p-6 fade-up" data-testid="email-limits-setting">
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-[#CCFF00]" />
        <div className="label-mono text-[#CCFF00]">EMAIL SENDING LIMITS</div>
      </div>
      <p className="text-xs text-[#A3A3A3] mt-2 max-w-xl">Maximum emails the app sends through Resend. Once a limit is reached, further emails are held back until the next day or month (UTC). Keep these within your Resend plan.</p>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mt-4 items-end max-w-xl">
        <label className="block">
          <span className="label-mono text-[#A3A3A3]">Per day</span>
          <input type="number" min={1} value={daily} onChange={(e) => setDaily(e.target.value)} data-testid="email-limits-daily" className={INPUT} />
        </label>
        <label className="block">
          <span className="label-mono text-[#A3A3A3]">Per month</span>
          <input type="number" min={1} value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid="email-limits-monthly" className={INPUT} />
        </label>
        <button type="button" onClick={() => setConfirm(true)} disabled={!valid || !changed} data-testid="email-limits-save"
          className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black disabled:opacity-40">Save</button>
      </div>
      {!valid && daily && monthly && <div className="text-xs text-[#FF4500] mt-2" data-testid="email-limits-error">Enter whole numbers above 0, with the daily limit no higher than the monthly limit.</div>}
      <Dialog open={confirm} onOpenChange={(o) => { if (!busy) setConfirm(o); }}>
        <DialogContent className="bg-[#0F0F0F] border border-[#262626] max-w-sm text-[#E5E5E5]" data-testid="email-limits-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Update email limits?</DialogTitle>
            <DialogDescription className="text-sm text-[#A3A3A3] pt-1">
              New limits: {d.toLocaleString()} per day and {m.toLocaleString()} per month (currently {saved.daily.toLocaleString()} / {saved.monthly.toLocaleString()}). Make sure your Resend plan allows this many.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setConfirm(false)} disabled={busy} data-testid="email-limits-cancel"
              className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
            <button type="button" onClick={save} disabled={busy} data-testid="email-limits-confirm"
              className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
